import { must } from './dom';

/**
 * A breaker-style switch row: one real button carrying `role="switch"`, so
 * the whole row is the touch target and a screen reader hears on/off. The
 * knob itself is drawn by the stylesheet from `aria-checked`.
 *
 * Returns the paint function, for state that can change underneath the
 * switch (the OS motion preference flipping while no override is set).
 */
export function bindSwitch(
  id: string,
  initial: boolean,
  onChange: (on: boolean) => void,
): (on: boolean) => void {
  const button = must(id);
  const paint = (on: boolean) => button.setAttribute('aria-checked', String(on));
  paint(initial);
  button.addEventListener('click', () => {
    const next = button.getAttribute('aria-checked') !== 'true';
    paint(next);
    onChange(next);
  });
  return paint;
}

/**
 * Settings that are one flag on <html> and nothing else — high contrast and
 * larger text. High contrast reaches further than colour (STYLEGUIDE §12): it
 * also thickens terrain linework, cases the wires and takes the ambient layer
 * off the board. All of it still keys off this attribute, because the sprites
 * read their stroke widths from tokens rather than baking them in.
 */
export type StyleFlag = 'data-contrast' | 'data-large-text';

export function applyFlag(flag: StyleFlag, on: boolean): void {
  document.documentElement.toggleAttribute(flag, on);
}

export function flagApplied(flag: StyleFlag): boolean {
  return document.documentElement.hasAttribute(flag);
}
